/**
 * Lightweight template resolver for CMS blocks.
 * Replaces {{variable.path}} with values from a context object.
 * Supports default values: {{customer.firstName | Guest}}
 * Unmatched variables pass through unchanged.
 */

function resolvePath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

export function renderTemplate(template: string, context: Record<string, unknown>): string {
  return template.replace(/\{\{([^}]+)\}\}/g, (match, expr) => {
    const [path, defaultValue] = expr.trim().split('|').map((s: string) => s.trim());
    const value = resolvePath(context, path);
    if (value === null || value === undefined) {
      // If there's an explicit default, use it; otherwise pass through unchanged
      return defaultValue ?? match;
    }
    return String(value);
  });
}

/**
 * Deep-resolve all string props that contain {{...}} placeholders.
 * Returns a new props object — never mutates the input.
 */
export function resolvePropsDeep(
  props: Record<string, unknown>,
  context: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (typeof v === 'string' && v.includes('{{')) {
      out[k] = renderTemplate(v, context);
    } else if (Array.isArray(v)) {
      out[k] = v.map((item) =>
        item && typeof item === 'object' && !Array.isArray(item)
          ? resolvePropsDeep(item as Record<string, unknown>, context)
          : item,
      );
    } else if (v && typeof v === 'object') {
      out[k] = resolvePropsDeep(v as Record<string, unknown>, context);
    } else {
      out[k] = v;
    }
  }
  return out;
}
